// ─── Portal Branding Presentation ──────────────────────────────────────────────
// Pure, DOM-free helpers shared by the branding provider and every branded
// surface (auth pages, sidebar, header).
//
// Branding is presentation only. It is never proof of Operator ownership: the
// backend's trusted ingress contract remains the sole tenant authority, and
// nothing here participates in authentication, authorization or request payloads.

import { APP_CONFIG } from '../app/config';
import {
  accentHoverTriplet,
  contrastTextTriplet,
  isValidHexColor,
  readableAccentTriplet,
} from '../lib/brandColor';

// Fixed AlgoSys presentation defaults. These mirror the backend's
// DEFAULT_OPERATOR_BRANDING_* constants and are the only fallback ever used —
// a failed or invalid response must never surface another portal's branding.
export const ALGOSYS_BRANDING_DEFAULTS = Object.freeze({
  businessName: 'AlgoSys',
  logoReference: null,
  primaryColor: '#E8A33D',
});

// Resolution states.
//
// `loading` and `unresolved` both carry the fixed AlgoSys default values but
// suppress AlgoSys-specific marketing, so a pending request or an unknown or
// unavailable portal never shows AlgoSys promotional copy, contact details or
// company attribution. The bundled AlgoSys logo is shown in these states only on
// a main-portal host (see `resolveBrandLogo`).
//
// `main` is the only state that enables that content, and it requires a
// positively classified main-portal hostname — never mere equality with the
// default branding values, because an approved Operator may legitimately
// configure the AlgoSys name and accent colour.
export const BRANDING_STATUS = Object.freeze({
  LOADING: 'loading',
  MAIN: 'main',
  BRANDED: 'branded',
  UNRESOLVED: 'unresolved',
});

// Matches the backend's `business_name` max_length. A longer value is treated as
// unexpected and discarded rather than truncated silently.
const MAX_BUSINESS_NAME_LENGTH = 120;

// The bundled asset is the AlgoSys platform logo. It renders only on a main
// AlgoSys portal host, and this alt text must never claim the mark belongs to an
// Operator. The Operator's identity is shown as separate text.
export const BUNDLED_LOGO_ALT = 'AlgoSys platform logo';

// Browser tab title while the portal is not positively identified. index.html
// uses the same value before the app script runs.
export const NEUTRAL_PORTAL_TITLE = 'Trading Workspace';

// Custom-logo request state published by the branding provider.
export const LOGO_STATUS = Object.freeze({
  NONE: 'none',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
});

// What a brand-logo slot renders when no custom logo is shown.
export const LOGO_FALLBACK = Object.freeze({
  BUNDLED: 'bundled',
  PLACEHOLDER: 'placeholder',
  NONE: 'none',
});

// White-label Phase 5C logo contract, mirroring the backend's
// app/identity/logo_storage.py limits. Client-side use is a convenience; the
// backend stays authoritative.
export const OPERATOR_LOGO_ALLOWED_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/webp']);
export const OPERATOR_LOGO_MAX_BYTES = 2 * 1024 * 1024;

// Exact shape of a backend-managed Operator logo reference
// (managed-operator-logos/v1/<48 hex>.png). Anything else is legacy metadata
// that must never be rendered or turned into an image URL.
const MANAGED_LOGO_REFERENCE_RE = /^managed-operator-logos\/v1\/[0-9a-f]{48}\.png$/;

export function isManagedLogoReference(value) {
  return typeof value === 'string' && MANAGED_LOGO_REFERENCE_RE.test(value);
}

export const NEUTRAL_AUTH_HEADLINE = 'Automated Trading Workspace';
export const NEUTRAL_AUTH_SUBHEAD =
  'Sign in to manage your strategies, broker connections and deployments.';

/**
 * Presentation-only classification of the browser hostname.
 *
 * This decides nothing except whether AlgoSys's own marketing content may be
 * displayed. It never selects an Operator, never authenticates, and never
 * affects backend tenant authorization; an unrecognised host only falls back to
 * neutral presentation.
 */
export function isMainPortalHostname(hostname) {
  if (typeof hostname !== 'string') return false;
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, '');
  if (!normalized) return false;
  return APP_CONFIG.mainPortalHostnames.includes(normalized);
}

function normalizeBusinessName(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_BUSINESS_NAME_LENGTH) return null;
  return trimmed;
}

// `logo_reference` is a validated relative metadata key, not a URL. Managed
// references (Phase 5C) are served only through the trusted /auth/portal-logo
// endpoint, and legacy metadata is deliberately never converted into an image
// source here. It is nullable in the contract, so an unexpected type is
// discarded rather than failing the whole response.
function normalizeLogoReference(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Strictly parse a `/auth/portal-branding` response.
 *
 * Returns `null` when the payload is not a usable rendition of the Phase 3A
 * contract — not a plain object, or missing/invalid in either of the two
 * non-nullable fields. Callers must treat `null` as a failed resolution and must
 * not cache it, so a malformed success can never become permanently cached
 * branding.
 */
export function parsePortalBrandingResponse(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const businessName = normalizeBusinessName(raw.business_name);
  if (businessName === null) return null;
  if (!isValidHexColor(raw.primary_color)) return null;

  const primaryColor = raw.primary_color;
  const logoReference = normalizeLogoReference(raw.logo_reference);

  return {
    businessName,
    logoReference,
    primaryColor,
    // Used only to decide whether an accent override is worth emitting; it is
    // deliberately not used to infer portal identity.
    matchesAlgosysDefaults:
      businessName === ALGOSYS_BRANDING_DEFAULTS.businessName
      && primaryColor.toUpperCase() === ALGOSYS_BRANDING_DEFAULTS.primaryColor.toUpperCase()
      && logoReference === null,
  };
}

/** Fixed-default branding state, used while loading and after any failure. */
export function fallbackBrandingState(status = BRANDING_STATUS.UNRESOLVED) {
  return { ...ALGOSYS_BRANDING_DEFAULTS, status };
}

/**
 * Build branding state from a validated response. `isMainPortal` comes from the
 * presentation-only hostname classification, so an Operator that happens to use
 * the AlgoSys defaults is still treated as a branded portal.
 *
 * Returns `null` for an unusable payload.
 */
export function brandingStateFromResponse(raw, { isMainPortal = false } = {}) {
  const parsed = parsePortalBrandingResponse(raw);
  if (!parsed) return null;

  return {
    businessName: parsed.businessName,
    logoReference: parsed.logoReference,
    primaryColor: parsed.primaryColor,
    matchesAlgosysDefaults: parsed.matchesAlgosysDefaults,
    status: isMainPortal ? BRANDING_STATUS.MAIN : BRANDING_STATUS.BRANDED,
  };
}

/**
 * CSS custom properties for an Operator accent, or `null` when there is nothing
 * to override. Returning `null` is what lets the provider remove the properties
 * entirely so the stylesheet defaults apply and nothing leaks between portals.
 */
export function brandAccentCssVars(branding, theme) {
  if (!branding || branding.status !== BRANDING_STATUS.BRANDED) return null;
  if (branding.matchesAlgosysDefaults) return null;
  const accent = readableAccentTriplet(branding.primaryColor, theme);
  if (!accent) return null;
  return {
    '--brand-accent': accent,
    '--brand-accent-hover': accentHoverTriplet(branding.primaryColor, theme),
    '--brand-accent-contrast': contrastTextTriplet(branding.primaryColor),
  };
}

/**
 * Brand-logo slot for every branded surface.
 *
 * - Main AlgoSys host: the bundled AlgoSys logo, including while branding loads
 *   or after it fails, exactly as before.
 * - Any other host: never the bundled AlgoSys logo. A neutral placeholder is
 *   shown while branding or the custom logo is still loading, and after an
 *   unresolved/failed branding lookup. A branded portal shows its loaded custom
 *   logo, or nothing when it has none (its business name is shown as text).
 *
 * `isMainPortalHost` is the presentation-only hostname classification; it never
 * selects an Operator or grants anything.
 */
export function resolveBrandLogo(branding) {
  const status = branding?.status ?? BRANDING_STATUS.UNRESOLVED;
  const isBranded = status === BRANDING_STATUS.BRANDED;

  if (status === BRANDING_STATUS.MAIN || (branding?.isMainPortalHost === true && !isBranded)) {
    return { logoSrc: null, logoAlt: BUNDLED_LOGO_ALT, logoFallback: LOGO_FALLBACK.BUNDLED };
  }
  if (!isBranded) {
    return { logoSrc: null, logoAlt: null, logoFallback: LOGO_FALLBACK.PLACEHOLDER };
  }

  const logoSrc = typeof branding.logoSrc === 'string' ? branding.logoSrc : null;
  if (logoSrc) {
    const name = branding.businessName || ALGOSYS_BRANDING_DEFAULTS.businessName;
    return { logoSrc, logoAlt: `${name} logo`, logoFallback: LOGO_FALLBACK.NONE };
  }
  return {
    logoSrc: null,
    logoAlt: null,
    logoFallback: branding.logoStatus === LOGO_STATUS.LOADING
      ? LOGO_FALLBACK.PLACEHOLDER
      : LOGO_FALLBACK.NONE,
  };
}

/**
 * Everything the auth pages need, resolved from one place so login, signup,
 * forgot-password, reset-password and verify-email stay consistent.
 */
export function resolveAuthPresentation(branding) {
  const status = branding?.status ?? BRANDING_STATUS.UNRESOLVED;
  const isMain = status === BRANDING_STATUS.MAIN;
  const isBranded = status === BRANDING_STATUS.BRANDED;
  const { logoSrc, logoAlt, logoFallback } = resolveBrandLogo(branding);
  // A pending request and a failed/unknown portal are both "not yet identified",
  // so neither may claim a business name or show AlgoSys marketing.
  const isIdentified = isMain || isBranded;
  const businessName = isIdentified
    ? (branding?.businessName || ALGOSYS_BRANDING_DEFAULTS.businessName)
    : null;

  return {
    status,
    isLoading: status === BRANDING_STATUS.LOADING,
    // AlgoSys-specific marketing copy, WhatsApp contact details and the company
    // attribution require a positively identified main AlgoSys portal.
    showAlgosysMarketing: isMain,
    businessName,
    logoSrc,
    logoAlt,
    logoFallback,
    // A branded portal always states its identity as text, whether or not it
    // has a custom logo.
    showBusinessNameBadge: isBranded,
    documentTitle: isMain
      ? 'AlgoSys Login | Cloud for Trading Automation'
      : (isBranded ? `${businessName} Login` : 'Login'),
    loginSubtitle: isIdentified
      ? `Sign in to your ${businessName} account.`
      : NEUTRAL_AUTH_SUBHEAD,
    // Non-breaking space while unidentified keeps the footer's height stable so
    // resolving branding does not shift the layout.
    footerText: isMain
      ? '© 2026 AlgoSys · A product of Traders Destination Academy LLP'
      : (isBranded ? `© 2026 ${businessName}` : '\u00A0'),
  };
}

/**
 * Sidebar brand block. The main portal keeps its existing logo-only appearance;
 * a branded portal shows its business name as text, under its custom logo when
 * one has loaded.
 */
export function resolveSidebarBrand(branding) {
  const isBranded = branding?.status === BRANDING_STATUS.BRANDED;
  return {
    showBusinessName: isBranded,
    businessName: isBranded ? branding.businessName : null,
    ...resolveBrandLogo(branding),
  };
}
